import { Alert, Button, Flex, Form, Input, Spin, Typography } from 'antd'
import { FormItem } from '../../../../components/form-item'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { StoreInput } from '../../../input/store-input'
import { DETAILS, useCreateCampaignStepsContext } from '../campaign-create-steps-context'
import { QuestionsList } from '../../questions-list'
import { CampaignLanguageSettings } from '../../campaign-language-settings'
import { ClarificationDepthField } from '../../clarification-depth-field'
import { InterviewInstructionsField } from '../../interview-instructions-field'
import { RegenerateQuestionsButton } from '../../../../components/regenerate-questions-button'
import { Paragraph } from '../paragraph'
import { Title } from '../title'
import { useCampaignLocale } from '../../../campaign-locale'
import clsx from 'clsx'

export function InterviewDetailsStep() {
    const { currentStep, analyzeJobOfferResult, editingJobOffer } = useCreateCampaignStepsContext()
    const reviewing = !!analyzeJobOfferResult && !editingJobOffer

    return (
        <div className={clsx({ hidden: currentStep !== DETAILS })}>
            <div className={clsx({ hidden: !reviewing })}>
                <ImproveOffer />
            </div>
            <div className={clsx({ hidden: reviewing })}>
                <InitialOffer />
            </div>
        </div>
    )
}

function InitialOffer() {
    const { t } = useTranslation()
    const { isAnalyzing } = useCreateCampaignStepsContext()

    return (
        <>
            <Title>{t('Job posting')}</Title>
            <Paragraph>
                {t('Paste your job posting. Evaa suggests screening questions that you can review and adjust.')}
            </Paragraph>
            <FormItem<StoreInput>
                name="title"
                label={t('Campaign name')}
                rules={[{ required: true, whitespace: true }]}
            >
                <Input
                    placeholder={t('Enter title')}
                    disabled={isAnalyzing}
                />
            </FormItem>
            <FormItem<StoreInput>
                name={['data', 'vacancy']}
                label={t('Job posting contents')}
                rules={[{ required: true, whitespace: true }, { min: 200 }]}
            >
                <Input.TextArea
                    placeholder={t('Paste the job posting here')}
                    autoSize={{ minRows: 6, maxRows: 25 }}
                    disabled={isAnalyzing}
                />
            </FormItem>
            <CampaignLanguageSettings
                interview
                disabled={isAnalyzing}
                className="mb-6"
            />
            <InitialOfferActions />
        </>
    )
}

function InitialOfferActions() {
    const { t } = useTranslation()
    const {
        current,
        isAnalyzing,
        previousStep,
        analyzeJobOfferResult,
        editingJobOffer,
        setEditingJobOffer,
        analyze,
        formProps,
    } = useCreateCampaignStepsContext()

    const handleContinue = () => {
        void formProps.form
            .validateFields(['title', ['data', 'vacancy'], 'locale'])
            .then(() => {
                if (analyzeJobOfferResult) {
                    setEditingJobOffer(false)
                    return
                }

                return analyze()
            })
            .catch(() => undefined)
    }

    return (
        <Flex
            justify="space-between"
            gap="small"
            wrap
        >
            {current > 0 ? (
                <Button
                    size="large"
                    disabled={isAnalyzing}
                    onClick={previousStep}
                >
                    {t('Back')}
                </Button>
            ) : (
                <div />
            )}
            <Button
                key={editingJobOffer ? 'edit' : 'initial'}
                loading={isAnalyzing}
                size="large"
                type="primary"
                onClick={handleContinue}
            >
                {t(analyzeJobOfferResult ? 'Review interview' : 'Generate questions')}
            </Button>
        </Flex>
    )
}

function ImproveOffer() {
    const { t } = useTranslation()
    const { isAnalyzing } = useCreateCampaignStepsContext()

    return (
        <>
            <Title>{t('Review interview')}</Title>
            <Paragraph>{t('Review the questions and mark answers that should lead to rejection.')}</Paragraph>
            <RegenerationNotice />
            <QuestionsSection />
            <ClarificationDepthField disabled={isAnalyzing} />
            <InterviewInstructionsField disabled={isAnalyzing} />
            <ImproveOfferActions />
        </>
    )
}

function RegenerationNotice() {
    const { t } = useTranslation()
    const { questionsNeedRegeneration, analyzedLocale } = useCreateCampaignStepsContext()

    const form = Form.useFormInstance()
    const locale = useCampaignLocale(form)

    if (!questionsNeedRegeneration) {
        return null
    }

    return (
        <Alert
            type="info"
            showIcon
            className="mb-6"
            message={t('These questions were generated in {{language}}. Generate them again to use {{newLanguage}}.', {
                language: t(`locales.${analyzedLocale}`),
                newLanguage: t(`locales.${locale}`),
            })}
        />
    )
}

function QuestionsSection() {
    const { t } = useTranslation()
    const { analyze, isAnalyzing, questionsNeedRegeneration, formProps } = useCreateCampaignStepsContext()
    const language = t(`locales.${useCampaignLocale(formProps.form)}`)

    return (
        <section aria-label={t('Questions')}>
            <Flex
                justify="space-between"
                align="center"
                gap="small"
                wrap
                className="mb-2"
            >
                <Typography.Text strong>{t('Questions')}</Typography.Text>
                <RegenerateQuestionsButton
                    onRegenerate={analyze}
                    loading={isAnalyzing}
                    label={
                        questionsNeedRegeneration ? t('Generate questions in {{language}}', { language }) : undefined
                    }
                />
            </Flex>
            <Spin spinning={isAnalyzing}>
                <FormItem<StoreInput> name={['data', 'questions']}>
                    <QuestionsList
                        addButtonText={t('Add question')}
                        placeholder={t('Enter question')}
                        showAutoReject
                    />
                </FormItem>
            </Spin>
        </section>
    )
}

function ImproveOfferActions() {
    const { t } = useTranslation()
    const { isAnalyzing, nextStep, setEditingJobOffer, questionsNeedRegeneration } = useCreateCampaignStepsContext()

    return (
        <Flex
            justify="space-between"
            gap="small"
            wrap
        >
            <Button
                disabled={isAnalyzing}
                size="large"
                onClick={() => setEditingJobOffer(true)}
            >
                {t('Back')}
            </Button>
            <Button
                disabled={isAnalyzing || questionsNeedRegeneration}
                size="large"
                type="primary"
                onClick={nextStep}
            >
                {t('Continue')}
            </Button>
        </Flex>
    )
}
